/**
 * Date utilities with Montreal timezone support
 */
import { parseISO } from 'date-fns';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';

// Fuseau horaire de Montréal
export const MONTREAL_TIMEZONE = 'America/Montreal';

/**
 * Format a date string in Montreal timezone
 * @param dateString - ISO date string or Date object
 * @param formatString - date-fns format string
 * @returns Formatted date in Montreal timezone
 */
export const formatDateMontreal = (
  dateString: string | Date,
  formatString: string = 'yyyy-MM-dd HH:mm'
): string => {
  try {
    let date: Date;
    
    if (typeof dateString === 'string') {
      // If the date string doesn't have a timezone, assume it's UTC
      // The backend sends dates in UTC without the 'Z' suffix
      const hasTimezone = dateString.endsWith('Z') || 
                          dateString.includes('+') || 
                          (dateString.includes('-') && dateString.lastIndexOf('-') > 10);
      
      const dateStr = hasTimezone ? dateString : dateString + 'Z';
      date = parseISO(dateStr);
    } else {
      date = dateString;
    }
    
    return formatInTimeZone(date, MONTREAL_TIMEZONE, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return typeof dateString === 'string' ? dateString : dateString.toISOString();
  }
};

/**
 * Convert a date to Montreal timezone
 * @param date - ISO date string or Date object
 * @returns Date object in Montreal timezone
 */
export const toMontrealTime = (date: string | Date): Date => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  return utcToZonedTime(parsedDate, MONTREAL_TIMEZONE);
};

/**
 * Get current time in Montreal
 * @returns Date object representing current time in Montreal
 */
export const nowInMontreal = (): Date => {
  return utcToZonedTime(new Date(), MONTREAL_TIMEZONE);
};

/**
 * Format date with default Montreal format (yyyy-MM-dd HH:mm)
 */
export const formatDateDefault = (dateString: string | Date): string => {
  return formatDateMontreal(dateString, 'yyyy-MM-dd HH:mm');
};

/**
 * Format date with time only (HH:mm:ss)
 */
export const formatTimeOnly = (dateString: string | Date): string => {
  return formatDateMontreal(dateString, 'HH:mm:ss');
};

/**
 * Format date with full timestamp (yyyy-MM-dd HH:mm:ss)
 */
export const formatDateFull = (dateString: string | Date): string => {
  return formatDateMontreal(dateString, 'yyyy-MM-dd HH:mm:ss');
};

